import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: white;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  padding: var(--space-12) var(--space-4);
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: var(--text-4xl);
  color: var(--primary-900);
  margin-bottom: var(--space-4);
  font-family: var(--font-serif);

  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const PageSubtitle = styled.p`
  font-size: var(--text-xl);
  color: var(--neutral-700);
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const ContentSection = styled.section`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-12) var(--space-4);

  @media (max-width: 768px) {
    padding: var(--space-8) var(--space-4);
  }
`;

const ContentBlock = styled.div`
  margin-bottom: var(--space-8);

  h2 {
    font-size: var(--text-2xl);
    color: var(--neutral-900);
    margin-bottom: var(--space-4);
    font-family: var(--font-serif);
  }

  p {
    font-size: var(--text-lg);
    line-height: 1.8;
    color: var(--neutral-800);
    margin-bottom: var(--space-4);
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      font-size: var(--text-lg);
      color: var(--neutral-800);
      margin-bottom: var(--space-3);
      padding-left: var(--space-6);
      position: relative;

      &::before {
        content: '→';
        position: absolute;
        left: 0;
        color: var(--primary-600);
        font-weight: bold;
      }
    }
  }
`;

const MissionBox = styled.div`
  background: var(--primary-50);
  border-left: 4px solid var(--primary-600);
  padding: var(--space-6);
  margin: var(--space-8) 0;
  border-radius: var(--radius-md);

  h3 {
    font-size: var(--text-xl);
    color: var(--primary-800);
    margin-bottom: var(--space-3);
    font-weight: 600;
  }

  p {
    font-size: var(--text-base);
    color: var(--neutral-700);
    margin: 0;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-6);
  margin-top: var(--space-6);
`;

const ValueCard = styled.div`
  background: var(--neutral-100);
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  text-align: center;

  h4 {
    font-size: var(--text-lg);
    color: var(--primary-700);
    margin-bottom: var(--space-3);
  }

  p {
    font-size: var(--text-base);
    color: var(--neutral-700);
    margin: 0;
  }
`;

const ContactSection = styled.div`
  background: var(--neutral-100);
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  text-align: center;
  margin-top: var(--space-8);

  h3 {
    font-size: var(--text-xl);
    color: var(--neutral-900);
    margin-bottom: var(--space-3);
  }

  p {
    font-size: var(--text-base);
    color: var(--neutral-700);
    margin-bottom: var(--space-4);
  }

  a {
    color: var(--primary-600);
    font-weight: 500;
  }
`;

const AboutPage = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle>About Intellica</PageTitle>
          <PageSubtitle>
            Where intellect meets analytics to create meaningful insights for the curious mind
          </PageSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ContentBlock>
          <h2>Our Mission</h2>
          <p>
            Intellica is dedicated to bridging the gap between complex ideas and accessible understanding. 
            We believe that in an age of information overload, what's needed isn't more content, but 
            better analysis and clearer perspectives.
          </p>
          <p>
            Our mission is to provide thoughtful, well-researched analyses of trends, innovations, and 
            ideas across various fields, making them comprehensible without sacrificing depth or accuracy.
          </p>
        </ContentBlock>

        <MissionBox>
          <h3>Our Approach</h3>
          <p>
            We combine intellectual rigor with analytical precision to deliver content that informs, 
            educates, and inspires. Every article is crafted to provide context, clarity, and 
            comprehensive understanding of complex topics.
          </p>
        </MissionBox>

        <ContentBlock>
          <h2>What We Cover</h2>
          <ul>
            <li>In-depth analysis of emerging trends across science, technology, and society</li>
            <li>Research highlights that translate academic findings into practical insights</li>
            <li>Thought pieces that challenge conventional wisdom and inspire new perspectives</li>
            <li>"Explained" series breaking down complex topics into digestible formats</li>
            <li>Reader Q&A columns addressing your most pressing questions</li>
            <li>Curated commentaries from leading thinkers and experts</li>
          </ul>
        </ContentBlock>

        <ContentBlock>
          <h2>Our Values</h2>
          <ValuesGrid>
            <ValueCard>
              <h4>Analytical Rigor</h4>
              <p>Every piece is thoroughly researched and fact-checked to ensure accuracy</p>
            </ValueCard>
            <ValueCard>
              <h4>Accessibility</h4>
              <p>Complex ideas presented in clear, engaging language without jargon</p>
            </ValueCard>
            <ValueCard>
              <h4>Intellectual Honesty</h4>
              <p>We present multiple perspectives and acknowledge uncertainty where it exists</p>
            </ValueCard>
            <ValueCard>
              <h4>Continuous Learning</h4>
              <p>We evolve with new information and welcome constructive dialogue</p>
            </ValueCard>
          </ValuesGrid>
        </ContentBlock>

        <ContentBlock>
          <h2>Why Intellica?</h2>
          <p>
            In a world where information moves at the speed of light, we slow down to think. 
            We don't chase clicks or sensationalize stories. Instead, we focus on providing 
            meaningful analysis that helps our readers understand not just what's happening, 
            but why it matters and what it means for the future.
          </p>
          <p>
            Whether you're a professional seeking industry insights, a student exploring new 
            ideas, or simply someone who enjoys intellectual stimulation, Intellica offers 
            content that respects your intelligence while expanding your understanding.
          </p>
        </ContentBlock>

        <ContactSection>
          <h3>Get in Touch</h3>
          <p>
            We value your feedback and suggestions. Have a topic you'd like us to explore? 
            Want to contribute your expertise? We'd love to hear from you.
          </p>
          <p>
            Email us at: <a href="mailto:info@intellica.it.com">info@intellica.it.com</a>
          </p>
        </ContactSection>
      </ContentSection>
    </PageContainer>
  );
};

export default AboutPage;